import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  CartesianGrid,
} from "recharts";

import { db } from "../../firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";

/* ---------------- Drive helpers (for image thumbnails) ---------------- */

function extractDriveId(rawUrl = "") {
  const url = String(rawUrl || "").trim();
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes("drive.google.com")) return null;
    const m = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (m && m[1]) return m[1];
    const q = u.searchParams.get("id");
    if (q) return q;
  } catch (_) {}
  return null;
}

function buildDriveImageCandidates(thumbnailUrl) {
  const id = extractDriveId(thumbnailUrl);
  if (!id) return [];
  return [
    `https://drive.usercontent.google.com/uc?id=${id}&export=view`,
    `https://drive.google.com/uc?export=view&id=${id}`,
    `https://lh3.googleusercontent.com/d/${id}=w1200-h800-no`,
    `https://drive.google.com/thumbnail?id=${id}&sz=w1200`,
  ];
}

function getYouTubeThumbnail(url) {
  const match = String(url || "").match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/
  );
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
}

function SmartThumb({
  course,
  className = "w-16 h-16 rounded-xl object-cover border",
}) {
  const youTubeThumb =
    Array.isArray(course?.youtubeLinks) && course.youtubeLinks[0]
      ? course.youtubeLinks[0].thumbnail ||
        getYouTubeThumbnail(course.youtubeLinks[0].url)
      : null;

  const candidates = [];
  const primaryThumb = course?.thumbnailUrl || course?.thumbnail;
  if (primaryThumb) candidates.push(...buildDriveImageCandidates(primaryThumb));
  if (youTubeThumb) candidates.push(youTubeThumb);
  candidates.push("https://placehold.co/160?text=No+Image");

  const [idx, setIdx] = useState(0);
  const src = candidates[Math.min(idx, candidates.length - 1)];

  return (
    <img
      src={src}
      alt={course?.title || "Thumbnail"}
      className={className}
      referrerPolicy="no-referrer"
      onError={() => {
        if (idx < candidates.length - 1) setIdx((n) => n + 1);
      }}
      draggable="false"
    />
  );
}

/* --------------------------- Drawer component --------------------------- */

const PAID_STATUSES = new Set(["paid", "completed", "approved"]);

export default function CourseAnalyticsDrawer({ open, onClose, course }) {
  const [series, setSeries] = useState([]); // [{date, sales}]
  const [salesCount, setSalesCount] = useState(0);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  // 7-day window labels
  const days = useMemo(() => {
    const out = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      const label = d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
      out.push({ key, label });
    }
    return out;
  }, []);

  // Live stream: all orders for this course → filter to last 7 days client-side
  useEffect(() => {
    if (!open || !course?.id) {
      setSeries([]);
      setSalesCount(0);
      return;
    }

    const start = new Date();
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);

    const qOrders = query(
      collection(db, "orders"),
      where("courseId", "==", course.id)
    );

    const unsub = onSnapshot(
      qOrders,
      (snap) => {
        // seed zeroes
        const map = new Map(
          days.map((d) => [d.key, { date: d.label, sales: 0 }])
        );

        snap.forEach((doc) => {
          const r = doc.data();

          // only count paid/approved orders
          const status = String(r.status || "").toLowerCase();
          if (!PAID_STATUSES.has(status)) return;

          // pick the best timestamp we have
          const at =
            r.approvedAt?.toDate?.() ||
            r.approvedAt || // already Date
            r.submittedAt?.toDate?.() ||
            r.createdAt?.toDate?.() ||
            new Date();

          // keep only last 7 days
          const d = new Date(at);
          if (d < start) return;

          d.setHours(0, 0, 0, 0);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(d.getDate()).padStart(2, "0")}`;

          if (map.has(key)) {
            const prev = map.get(key);
            prev.sales += 1; // 1 sale per paid order
            map.set(key, prev);
          }
        });

        const arr = Array.from(map.values());
        setSeries(arr);
        setSalesCount(arr.reduce((t, x) => t + x.sales, 0));
      },
      (err) => {
        console.error("[CourseAnalyticsDrawer] orders stream failed:", err);
        const arr = days.map((d) => ({ date: d.label, sales: 0 }));
        setSeries(arr);
        setSalesCount(0);
      }
    );

    return () => unsub();
  }, [open, course?.id, days]);

  if (!open || !course) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="bg-white w-full max-w-md h-full shadow-2xl px-8 py-8 overflow-y-auto relative rounded-l-2xl flex flex-col">
        <button
          className="absolute top-4 right-6 text-2xl text-gray-400 hover:text-gray-700"
          onClick={onClose}
        >
          ×
        </button>

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <SmartThumb course={course} />
          <div>
            <div className="text-xl font-bold">{course.title}</div>
            <div className="text-xs text-blue-600 capitalize font-semibold">
              {course.type}
            </div>
            <div className="mt-1">
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-semibold
                ${
                  course.status === "approved"
                    ? "bg-green-100 text-green-700"
                    : course.status === "pending"
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {course.status}
              </span>
            </div>
          </div>
        </div>

        <div className="mb-2 text-gray-700 text-sm">{course.description}</div>
        <div className="mb-6 text-xs text-gray-400">
          Added: {course.createdAt?.toDate?.().toLocaleString?.() || "Unknown"}
        </div>

        {/* --- Stats --- */}
        <div className="flex gap-8 mb-8">
          <div>
            <div className="font-bold text-lg">{salesCount}</div>
            <div className="text-xs text-gray-500">Sales (7 days)</div>
          </div>
          <div>
            <div className="font-bold text-lg">
              {course.price === 0 || course.price === "0"
                ? "Free"
                : `$${course.price}`}
            </div>
            <div className="text-xs text-gray-500">Price</div>
          </div>
        </div>

        {/* --- Chart --- */}
        <div className="bg-blue-50 rounded-xl p-4 mb-8">
          <div className="text-sm font-semibold text-blue-700 mb-2">
            Sales (Past 7 Days)
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} />
              <ChartTooltip />
              <Line
                type="monotone"
                dataKey="sales"
                stroke="#2563eb"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* --- YouTube Links --- */}
        {course.youtubeLinks && course.youtubeLinks.length > 0 && (
          <div className="mb-6">
            <div className="font-semibold mb-2">YouTube Links</div>
            <div className="flex flex-col gap-2">
              {course.youtubeLinks.map((link, i) =>
                link.url ? (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 underline text-xs flex items-center gap-1"
                  >
                    {link.url}
                    {link.showFree ? (
                      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xxs ml-1">
                        Free
                      </span>
                    ) : (
                      <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded text-xxs ml-1">
                        Paid
                      </span>
                    )}
                  </a>
                ) : null
              )}
            </div>
          </div>
        )}

        {/* --- PDF Download --- */}
        {course.pdfUrl && (
          <div className="mt-4">
            <div className="font-semibold mb-1">PDF Material</div>
            <a
              href={course.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-semibold hover:bg-blue-700 transition"
            >
              Download/View PDF
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
